<script setup lang="ts">
import { computed } from 'vue'
import { PaginationMeta } from '../../services/api'

interface PaginationPageItem {
  key: string
  type: 'page' | 'ellipsis'
  value: number
}

const props = withDefaults(
  defineProps<{
    pagination: PaginationMeta | null
    maxVisiblePages?: number
  }>(),
  {
    maxVisiblePages: 7,
  }
)

const emit = defineEmits<{
  (e: 'page-change', page: number): void
}>()

const currentPage = computed(() => {
  return props.pagination?.currentPage ?? 1
})

const lastPage = computed(() => {
  return props.pagination?.lastPage ?? 1
})

const isFirstPage = computed(() => currentPage.value <= 1)
const isLastPage = computed(() => currentPage.value >= lastPage.value)
const shouldRender = computed(() => lastPage.value > 1)

function goToPage(page: number): void {
  if (!props.pagination) {
    return
  }

  if (page < 1 || page > lastPage.value) {
    return
  }

  if (page === currentPage.value) {
    return
  }

  emit('page-change', page)
}

function createPage(value: number): PaginationPageItem {
  return {
    key: `page-${value}`,
    type: 'page',
    value,
  }
}

function createEllipsis(key: string): PaginationPageItem {
  return {
    key,
    type: 'ellipsis',
    value: -1,
  }
}

const visiblePages = computed(() => {
  const totalPages = lastPage.value
  const activePage = currentPage.value
  const maxVisible = Math.max(5, props.maxVisiblePages)

  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, index) => createPage(index + 1))
  }

  const pages: PaginationPageItem[] = []
  const siblingCount = Math.max(1, Math.floor((maxVisible - 5) / 2))

  let startPage = activePage - siblingCount
  let endPage = activePage + siblingCount

  if (startPage <= 2) {
    startPage = 2
    endPage = startPage + siblingCount * 2
  }

  if (endPage >= totalPages - 1) {
    endPage = totalPages - 1
    startPage = endPage - siblingCount * 2
  }

  startPage = Math.max(2, startPage)
  endPage = Math.min(totalPages - 1, endPage)

  pages.push(createPage(1))

  if (startPage > 2) {
    pages.push(createEllipsis('ellipsis-start'))
  }

  for (let page = startPage; page <= endPage; page++) {
    pages.push(createPage(page))
  }

  if (endPage < totalPages - 1) {
    pages.push(createEllipsis('ellipsis-end'))
  }

  pages.push(createPage(totalPages))

  return pages
})
</script>

<template>
  <nav v-if="shouldRender" aria-label="Pagination">
    <ul class="pagination mb-0 justify-content-center">
      <li class="page-item" :class="{ disabled: isFirstPage }">
        <button
          class="page-link"
          type="button"
          :disabled="isFirstPage"
          aria-label="Previous page"
          @click="goToPage(currentPage - 1)"
        >
          &lt;
        </button>
      </li>

      <li
        v-for="page in visiblePages"
        :key="page.key"
        class="page-item"
        :class="{
          active: page.type === 'page' && page.value === currentPage,
          disabled: page.type === 'ellipsis',
        }"
      >
        <button
          v-if="page.type === 'page'"
          class="page-link"
          type="button"
          @click="goToPage(page.value)"
        >
          {{ page.value }}
        </button>

        <span v-else class="page-link"> &hellip; </span>
      </li>

      <li class="page-item" :class="{ disabled: isLastPage }">
        <button
          class="page-link"
          type="button"
          :disabled="isLastPage"
          aria-label="Next page"
          @click="goToPage(currentPage + 1)"
        >
          >
        </button>
      </li>
    </ul>
  </nav>
</template>
